import cx from 'classnames';
import {Fragment, PureComponent, createRef} from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';

import {ConfirmModal, Icon} from 'components';
import {NOT_ALLOWED_ERROR} from 'constants/webrtc';
import {ScreenSharingActivityStatus} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {logger} from 'utils';

import {GroupRoomManager, PeerConnectionId, RoomEvent} from '../shared';

import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.WEBRTC.SCREEN_SHARING;

type Props = {
    readonly room: GroupRoomManager;
    readonly isPresenter: boolean;
    readonly mini?: boolean;
    readonly messageClassName?: string;
    readonly confirmRequired?: boolean;
    readonly onStop?: (error?: any) => void;
    readonly onStart?: () => void;
    readonly status: ScreenSharingActivityStatus;
} & WithTranslation;

type State = {
    sharingMessageEnabled: boolean;
    confirmPresenterDialogOpen: boolean;
};

const initialState: State = {
    sharingMessageEnabled: true,
    confirmPresenterDialogOpen: false
};

// A component was copied (TwilioScreenSharing) and modified
// TODO: re-implement with hooks
class KurentoScreenSharing extends PureComponent<Props, State> {
    readonly state: State = initialState;

    subscriptions = [];
    remoteMediaRef = createRef<HTMLDivElement>();

    componentDidMount() {
        if (this.props.isPresenter) {
            if (this.props.confirmRequired) {
                this.setState({confirmPresenterDialogOpen: true});
            } else {
                this.startAsPresenter();
            }
        } else {
            this.startAsViewer();
        }
    }

    componentWillUnmount() {
        if (this.props.isPresenter && this.props.status === ScreenSharingActivityStatus.Accepted) {
            this.stopScreenSharing();
        }
        this.props.room.stopPeerConnection(PeerConnectionId.ScreenSharing);
        this.subscriptions.forEach((unsubscribe) => unsubscribe());
    }

    closeConfirmPresenterDialogOpen = (confirm: boolean) => {
        this.setState({confirmPresenterDialogOpen: false});
        if (confirm) {
            this.startAsPresenter();
        } else {
            this.stopScreenSharing({message: this.props.t(T_KEYS.REJECTED)});
        }
    };

    startAsViewer = () => {
        if (this.props.room.isRoomStateConnected) {
            this.startViewerPeerConnection();
        }

        this.subscriptions.push(this.props.room.on(RoomEvent.Connected, this.startViewerPeerConnection));
    };

    startViewerPeerConnection = () => {
        this.props.room.startPeerConnection(PeerConnectionId.ScreenSharing, {
            constraints: {audio: false, video: false},
            mainRemoteParticipantElement: this.remoteMediaRef.current,
            mediaElementProperties: {muted: true}
        });
    };

    startAsPresenter = () => {
        const mediaDevices = navigator.mediaDevices as any;

        if (!mediaDevices || !mediaDevices.getDisplayMedia) {
            this.stopScreenSharing({message: T_KEYS.NOT_SUPPORTED});
            return;
        }

        if (this.props.room.isRoomStateConnected) {
            this.startPresenterPeerConnection();
        }

        this.subscriptions.push(this.props.room.on(RoomEvent.Connected, this.startPresenterPeerConnection));
    };

    startPresenterPeerConnection = async () => {
        try {
            const mediaDevices = navigator.mediaDevices as any;
            const videoStream = await mediaDevices.getDisplayMedia({audio: false});
            const screenSharingTrack = videoStream.getVideoTracks()[0];
            screenSharingTrack.addEventListener('ended', this.handleScreenSharingTrackEnded.bind(this));

            this.props.room.startPeerConnection(PeerConnectionId.ScreenSharing, {
                constraints: {audio: false, video: false},
                videoStream,
                mediaElementProperties: {muted: true}
            });

            this.props.onStart?.();
            this.enableSharingMessage();
        } catch (e: any) {
            const message = this.props.t(e.name === NOT_ALLOWED_ERROR ? T_KEYS.NOT_ALLOWED : T_KEYS.ERROR);
            logger.warn({category: logger.category.WebRTC, message: `Cannot start screen sharing: ${e.message}`});
            this.stopScreenSharing({message});
        }
    };

    stopScreenSharing = (error?: any) => {
        this.props.onStop?.(error);
    };

    handleScreenSharingTrackEnded = () => {
        const {t} = this.props;
        this.stopScreenSharing({message: t(T_KEYS.REJECTED)});
    };

    enableSharingMessage = () => {
        this.setState({sharingMessageEnabled: true});
    };

    dismissSharingMessage = () => {
        this.setState({sharingMessageEnabled: false});
    };

    render() {
        const {t, isPresenter, status, messageClassName, confirmRequired, mini} = this.props;
        const {
            remoteMediaRef, dismissSharingMessage, closeConfirmPresenterDialogOpen,
            state: {sharingMessageEnabled, confirmPresenterDialogOpen}
        } = this;

        return (
            <Fragment>
                {
                    isPresenter ?
                        (
                            status === ScreenSharingActivityStatus.Accepted && sharingMessageEnabled &&
                                <div className={cx(messageClassName,
                                    'tw-text-center tw-z-1 tw-px-5 tw-py-3 tw-bg-layout tw-shadow-md tw-rounded-md',
                                    'tw-flex tw-items-center')}
                                >
                                    <div className='tw-bg-default tw-rounded-full tw-flex-center tw-w-8 tw-h-8 tw-mr-4'>
                                        <Icon name='share-screen-out' size='sm' />
                                    </div>
                                    {t(T_KEYS.IN_PROGRESS)}
                                    <Icon
                                        name='close'
                                        button
                                        margin={false}
                                        className='tw-ml-4'
                                        color='dark'
                                        onClick={dismissSharingMessage}
                                    />
                                </div>
                        ) : (
                            !mini &&
                                <div>
                                    <div
                                        ref={remoteMediaRef}
                                        className='screen-sharing-video-container tw-flex-center tw-absolute tw-inset-0'
                                    />
                                </div>
                        )
                }
                {confirmRequired &&
                    <ConfirmModal
                        open={confirmPresenterDialogOpen}
                        title={t(T_KEYS.REQUESTED_MESSAGE)}
                        okText={t(TRANSLATION_KEYS.COMMON.BUTTONS.ALLOW)}
                        cancelText={t(TRANSLATION_KEYS.COMMON.BUTTONS.REJECT)}
                        onClose={closeConfirmPresenterDialogOpen}
                    />}
            </Fragment>
        );
    }
}

export default withTranslation()(KurentoScreenSharing);
